import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { notificationService } from '../services/notificationService';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [isEnabled, setIsEnabled] = useState(false);
  const [watchlistMarkets, setWatchlistMarkets] = useState([]);
  const wsRef = useRef(null);

  // Get WebSocket URL
  const getWsUrl = useCallback(() => {
    const url = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    return `${url}/api/ws`;
  }, []);

  // Connect to WebSocket for price monitoring
  const connectWebSocket = useCallback(() => {
    if (!isEnabled || watchlistMarkets.length === 0) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(getWsUrl());
      
      ws.onopen = () => {
        console.log('Notification WebSocket connected');
        // Subscribe to all watchlist markets
        watchlistMarkets.forEach(marketId => {
          ws.send(JSON.stringify({ type: 'subscribe', market_id: marketId }));
        });
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'price_update' && data.market_id) {
            // Check if this market is in watchlist
            if (watchlistMarkets.includes(data.market_id)) {
              const yesPrice = Math.round((data.prices?.YES_price_bp || 5000) / 100);
              const noPrice = Math.round((data.prices?.NO_price_bp || 5000) / 100);
              
              notificationService.checkPriceUpdate(
                data.market_id,
                data.market_title || 'Market',
                yesPrice,
                noPrice
              );
            }
          }
        } catch (e) {
          console.error('Failed to parse notification WebSocket message:', e);
        }
      };

      ws.onclose = () => {
        console.log('Notification WebSocket disconnected');
        wsRef.current = null;
        // Will be reconnected by effect
      };

      ws.onerror = (error) => {
        console.error('Notification WebSocket error:', error);
        ws.close();
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to create notification WebSocket:', error);
    }
  }, [isEnabled, watchlistMarkets, getWsUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Connect WebSocket when enabled and have watchlist
  useEffect(() => {
    if (isEnabled && watchlistMarkets.length > 0) {
      connectWebSocket();
    }
    
    // Reconnect interval
    const interval = setInterval(() => {
      if (isEnabled && watchlistMarkets.length > 0 && !wsRef.current) {
        connectWebSocket();
      }
    }, 15000);
    
    const ws = wsRef.current;
    return () => {
      clearInterval(interval);
      if (ws) {
        ws.close();
      }
    };
  }, [isEnabled, watchlistMarkets, connectWebSocket]);

  // Check notification permission on mount
  useEffect(() => {
    const checkPermission = () => {
      const enabled = notificationService.isEnabled();
      if (enabled !== isEnabled) {
        setIsEnabled(enabled);
      }
    };
    
    // Check permission after a slight delay to avoid cascade
    const timeout = setTimeout(checkPermission, 100);
    
    // Register service worker
    if ('serviceWorker' in navigator) {
      notificationService.registerServiceWorker();
    }
    
    return () => clearTimeout(timeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch watchlist when authenticated
  useEffect(() => {
    const fetchWatchlist = async () => {
      if (isAuthenticated) {
        try {
          const data = await apiService.getWatchlist();
          const marketIds = (data.watchlist || []).map(item => item.market_id);
          setWatchlistMarkets(marketIds);
          notificationService.setWatchlist(marketIds);
        } catch (error) {
          console.error('Failed to fetch watchlist for notifications:', error);
        }
      }
    };

    fetchWatchlist();
  }, [isAuthenticated]);

  const requestPermission = useCallback(async () => {
    const granted = await notificationService.requestPermission();
    setIsEnabled(granted);
    return granted;
  }, []);

  const enableNotifications = useCallback(async () => {
    if (!isEnabled) {
      return await requestPermission();
    }
    return true;
  }, [isEnabled, requestPermission]);

  const sendTestNotification = useCallback(async () => {
    return await notificationService.showTestNotification();
  }, []);

  const addMarketToWatch = useCallback((marketId) => {
    setWatchlistMarkets(prev => {
      const updated = [...new Set([...prev, marketId])];
      notificationService.setWatchlist(updated);
      return updated;
    });
  }, []);

  const removeMarketFromWatch = useCallback((marketId) => {
    setWatchlistMarkets(prev => {
      const updated = prev.filter(id => id !== marketId);
      notificationService.setWatchlist(updated);
      return updated;
    });
  }, []);

  const checkPriceUpdate = useCallback((marketId, marketTitle, yesPrice, noPrice) => {
    if (isEnabled) {
      notificationService.checkPriceUpdate(marketId, marketTitle, yesPrice, noPrice);
    }
  }, [isEnabled]);

  // ---- Real Web Push (VAPID) subscription ----
  const [isPushSubscribed, setIsPushSubscribed] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = useCallback(() => {
    // Prefer JWT (email/password users), fall back to session_token which the
    // backend `get_user_any` dep accepts as a Bearer too. Google-OAuth users
    // also have the session cookie sent automatically via credentials:include.
    const jwt = localStorage.getItem('access_token');
    const sess = localStorage.getItem('session_token');
    const token = jwt || sess;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Check current push subscription state on mount / auth change
  useEffect(() => {
    const check = async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setIsPushSubscribed(!!sub);
      } catch {
        setIsPushSubscribed(false);
      }
    };
    check();
  }, [isAuthenticated]);

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const output = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
    return output;
  };

  const subscribeToPush = useCallback(async () => {
    if (!isAuthenticated) {
      throw new Error('Please sign in before enabling push notifications.');
    }
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      throw new Error('Push notifications are not supported in this browser.');
    }

    // Notification API is not universally available (Safari iOS private
    // mode, WKWebView, older iOS versions expose window.Notification as
    // undefined). Guard every reference with typeof.
    const hasNotification = typeof window !== 'undefined' && typeof window.Notification !== 'undefined';
    if (!hasNotification) {
      throw new Error('Push notifications are not supported in this browser.');
    }

    // Ensure notification permission
    if (window.Notification.permission === 'denied') {
      throw new Error(
        'Notifications are blocked for this site in your browser. ' +
        'Click the lock/shield icon in the address bar → Site settings → ' +
        'set "Notifications" to Allow, then try again.'
      );
    }
    if (window.Notification.permission !== 'granted') {
      const perm = await window.Notification.requestPermission();
      if (perm === 'denied') {
        throw new Error(
          'You blocked notifications. To enable them, click the lock icon in ' +
          'the address bar → Site settings → Notifications → Allow.'
        );
      }
      if (perm !== 'granted') {
        throw new Error('Please allow notifications when prompted by your browser.');
      }
      setIsEnabled(true);
    }

    // Fetch public key from server
    const keyRes = await fetch(`${API_URL}/api/push/public-key`, {
      credentials: 'include',
    });
    const { public_key: vapidKey } = await keyRes.json();
    if (!vapidKey) throw new Error('Server is missing VAPID key.');

    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    }

    const body = sub.toJSON();
    const res = await fetch(`${API_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      credentials: 'include',
      body: JSON.stringify({
        endpoint: body.endpoint,
        keys: body.keys,
        user_agent: navigator.userAgent,
      }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Subscribe failed: ${res.status} ${text}`);
    }
    setIsPushSubscribed(true);
    return true;
  }, [isAuthenticated, API_URL, getAuthHeaders]);

  const unsubscribeFromPush = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return false;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) {
      setIsPushSubscribed(false);
      return true;
    }
    const endpoint = sub.endpoint;
    try {
      await fetch(`${API_URL}/api/push/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ endpoint }),
      });
    } catch (unsubErr) {
      // ignore — still unsubscribe locally
      console.debug('Push unsubscribe API call failed:', unsubErr?.message);
    }
    await sub.unsubscribe();
    setIsPushSubscribed(false);
    return true;
  }, [API_URL, getAuthHeaders]);

  const sendServerTestPush = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/push/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      credentials: 'include',
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Test push failed: ${res.status} ${text}`);
    }
    return res.json();
  }, [API_URL, getAuthHeaders]);

  const value = {
    isEnabled,
    isSupported: notificationService.isSupported(),
    permission: notificationService.getPermission(),
    watchlistMarkets,
    requestPermission,
    enableNotifications,
    sendTestNotification,
    addMarketToWatch,
    removeMarketFromWatch,
    checkPriceUpdate,
    // Real web push
    isPushSubscribed,
    subscribeToPush,
    unsubscribeFromPush,
    sendServerTestPush,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
